export const FOREGROUND_DIRECT_MESSAGE_TITLE = "New ReelitPro message";
export const FOREGROUND_DIRECT_MESSAGE_BODY =
  "You have a new message in ReelitPro.";
export const FOREGROUND_DIRECT_MESSAGE_LINK_KEY =
  "reelitproDirectMessageLink";

function safeString(value, maximumLength = 255) {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (
    !normalized ||
    normalized.length > maximumLength ||
    Array.from(normalized).some((character) => character.charCodeAt(0) < 32)
  ) {
    return null;
  }
  return normalized;
}

function appOrigin(locationObject) {
  try {
    const origin = new URL(locationObject?.origin || locationObject?.href).origin;
    return origin.startsWith("https://") ? origin : null;
  } catch {
    return null;
  }
}

export function buildForegroundDirectMessageNotification(
  payload,
  { locationObject = typeof window === "undefined" ? null : window.location } = {}
) {
  if (
    payload?.data?.event_type !== "message" ||
    payload?.data?.target_kind !== "conversation" ||
    payload?.notification?.title !== FOREGROUND_DIRECT_MESSAGE_TITLE ||
    payload?.notification?.body !== FOREGROUND_DIRECT_MESSAGE_BODY
  ) {
    return null;
  }
  const actorId = safeString(payload.data.actor_id);
  const origin = appOrigin(locationObject);
  if (!actorId || !origin) return null;

  const link = new URL("/messages", origin);
  link.searchParams.set("chat", actorId);
  const icon = new URL("/logo192.png", origin).href;
  const messageId = safeString(payload.messageId);
  const options = {
    body: FOREGROUND_DIRECT_MESSAGE_BODY,
    icon,
    badge: icon,
    data: { [FOREGROUND_DIRECT_MESSAGE_LINK_KEY]: link.href },
    renotify: false,
  };
  if (messageId) options.tag = `reelitpro-direct-message-${messageId}`;
  return { title: FOREGROUND_DIRECT_MESSAGE_TITLE, options };
}

export async function displayForegroundDirectMessageNotification(
  registration,
  payload,
  options = {}
) {
  if (typeof registration?.showNotification !== "function") return false;
  const notification = buildForegroundDirectMessageNotification(payload, options);
  if (!notification) return false;
  await registration.showNotification(notification.title, notification.options);
  return true;
}

function foregroundMessageLink(notification, workerGlobal) {
  const rawLink = notification?.data?.[FOREGROUND_DIRECT_MESSAGE_LINK_KEY];
  if (typeof rawLink !== "string") return null;
  try {
    const link = new URL(rawLink);
    const origin = new URL(workerGlobal.location.origin);
    if (
      link.origin !== origin.origin ||
      link.pathname !== "/messages" ||
      !safeString(link.searchParams.get("chat"))
    ) {
      return null;
    }
    return link.href;
  } catch {
    return null;
  }
}

async function openForegroundMessageLink(workerGlobal, link) {
  const clients = await workerGlobal.clients.matchAll({
    type: "window",
    includeUncontrolled: true,
  });
  const matchingClient = clients.find((client) => client.url === link);
  if (matchingClient?.focus) return matchingClient.focus();
  return workerGlobal.clients.openWindow(link);
}

export function installForegroundDirectMessageNotificationClickHandler(
  workerGlobal
) {
  workerGlobal.addEventListener("notificationclick", (event) => {
    const link = foregroundMessageLink(event.notification, workerGlobal);
    if (!link) return;
    event.stopImmediatePropagation();
    event.notification.close();
    event.waitUntil(openForegroundMessageLink(workerGlobal, link));
  });
}
